import React, { useState, useRef } from 'react';
import { presignUpload, uploadToS3 } from '../api/community';
import { compressImage } from '../utils/imageCompress';

const ACCEPT = 'image/jpeg,image/png,image/webp';

// Multi-file image uploader for community posts.
// Props:
//   token   — Cognito idToken (required)
//   max     — max images allowed for this post's category (review:3, qna:1, ...)
//   value   — current imageUrls (string[])
//   onChange(nextUrls) — array setter
//   disabled
export default function PostImageUploader({ token, max, value, onChange, disabled }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const remaining = max - value.length;
  const canAdd = remaining > 0 && !busy && !disabled;

  async function handleSelect(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    if (files.length > remaining) {
      setError(`최대 ${max}장까지 (남은 ${remaining}장)`);
      return;
    }
    setError(null);
    setBusy(true);
    const uploaded = [];
    try {
      for (let i = 0; i < files.length; i++) {
        setProgress({ current: i + 1, total: files.length });
        const compressed = await compressImage(files[i]);
        const presigned = await presignUpload({ token, contentType: compressed.type });
        const { imageUrl } = await uploadToS3(presigned, compressed);
        uploaded.push(imageUrl);
      }
      onChange([...value, ...uploaded]);
    } catch (err) {
      console.error('[PostImageUploader] upload failed', err);
      setError(err.message || '업로드 실패');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  function handleRemove(url) {
    onChange(value.filter(u => u !== url));
  }

  if (max === 0) return null;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={!canAdd}
          style={{
            padding: '8px 14px',
            border: '1px solid #d0d7de',
            borderRadius: 6,
            background: canAdd ? '#fff' : '#f6f8fa',
            cursor: canAdd ? 'pointer' : 'not-allowed',
            fontSize: 14,
          }}
        >
          📷 사진 추가
        </button>
        <span style={{ fontSize: 13, color: '#57606a' }}>
          {value.length} / {max}장
          {busy && progress && ` · 업로드 중 ${progress.current}/${progress.total}`}
        </span>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple={max > 1}
        accept={ACCEPT}
        onChange={handleSelect}
        style={{ display: 'none' }}
      />
      {error && (
        <div style={{ color: '#cf222e', fontSize: 13, marginBottom: 8 }}>{error}</div>
      )}
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {value.map(url => (
            <div key={url} style={{ position: 'relative', width: 90, height: 90 }}>
              <img
                src={url}
                alt="첨부 이미지"
                style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid #d0d7de' }}
                loading="lazy"
                decoding="async"
              />
              <button
                type="button"
                onClick={() => handleRemove(url)}
                disabled={busy || disabled}
                aria-label="이미지 삭제"
                style={{
                  position: 'absolute', top: -6, right: -6,
                  width: 22, height: 22, borderRadius: '50%',
                  border: 'none', background: '#cf222e', color: '#fff',
                  fontSize: 12, lineHeight: 1, cursor: busy ? 'not-allowed' : 'pointer',
                }}
              >×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
